"use client";

import React from "react";
import { format, parseISO } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Gift,
  Image as ImageIcon,
  Wallet,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { useBookingFlow } from "@/hooks/use-booking-flow";
import { Button } from "@/components/ui/button";
import { BookingStatus } from "./BookingStatus";
import { BookingFlowConfig } from "./booking-flow-config";
import { StepProgressBar } from "./StepProgressBar";
import { AddonsStep } from "./steps/AddonsStep";
import { DateTimeStep } from "./steps/DateTimeStep";
import { FormStep } from "./steps/FormStep";
import { PackageStep } from "./steps/PackageStep";
import { ReviewStep } from "./steps/ReviewStep";
import { ThemeStep } from "./steps/ThemeStep";

interface BookingStepsProps {
  config: BookingFlowConfig;
}

export const BookingSteps: React.FC<BookingStepsProps> = ({ config }) => {
  const flow = useBookingFlow(config);
  const stepNumber = flow.currentStepIndex + 1;

  if (flow.submittedBooking) {
    return (
      <BookingStatus
        booking={flow.submittedBooking}
        config={config}
        onReset={flow.status.reset}
      />
    );
  }

  const renderStep = () => {
    switch (flow.currentStep) {
      case "PACKAGE":
        return <PackageStep {...flow.packageStep} />;
      case "THEME":
        return <ThemeStep {...flow.themeStep} />;
      case "DATE_TIME":
        return <DateTimeStep {...flow.dateTimeStep} />;
      case "ADDONS":
        return <AddonsStep {...flow.addonsStep} />;
      case "CUSTOMER":
        return <FormStep {...flow.formStep} />;
      case "REVIEW":
        return <ReviewStep {...flow.reviewStep} />;
      default:
        return null;
    }
  };

  return (
    <div className="relative container mx-auto max-w-7xl px-4 py-6 lg:px-6 lg:py-12">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between lg:mb-10">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/70">
            Step {stepNumber} of {flow.totalSteps}
          </p>
          <h1 className="mt-2 text-2xl font-serif text-foreground md:text-3xl">
            {config.stepTitles[flow.currentStep]}
          </h1>
        </div>
      </div>

      <div className="mb-8 lg:mb-10">
        <StepProgressBar
          currentStepIndex={flow.currentStepIndex}
          steps={flow.steps}
        />
      </div>

      <div className="grid grid-cols-1 items-start gap-8 xl:grid-cols-12">
        <div
          className={cn(
            "flex min-w-0 flex-col transition-all duration-500",
            flow.showSummary
              ? "xl:col-span-8"
              : "mx-auto w-full max-w-5xl xl:col-span-12"
          )}
        >
          <div className="min-h-[650px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={flow.currentStep}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="h-full"
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-8 flex flex-col gap-4 border-t border-border/60 pt-6 md:flex-row md:items-center md:justify-end">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button
                variant="ghost"
                onClick={flow.navigation.goBack}
                disabled={flow.navigation.isBackDisabled}
                className="h-12 rounded-full border border-border/60 bg-background/30 px-6 text-muted-foreground transition-colors hover:bg-background/50 hover:text-foreground"
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>

              <Button
                onClick={flow.navigation.handlePrimaryAction}
                disabled={flow.navigation.isPrimaryDisabled}
                className="h-12 min-w-[180px] rounded-full bg-primary px-8 font-medium text-primary-foreground shadow-[0_16px_40px_rgba(0,0,0,0.2)] transition-all hover:bg-primary/90"
              >
                {flow.navigation.isSubmitting ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
                ) : (
                  <>
                    <span className="tracking-tight">
                      {flow.navigation.primaryLabel}
                    </span>
                    {flow.navigation.showPrimaryChevron && (
                      <ChevronRight className="ml-2 h-4 w-4" />
                    )}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {flow.showSummary && (
          <motion.aside
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            className="space-y-6 xl:col-span-4 xl:sticky xl:top-8"
          >
            <div className="relative overflow-hidden rounded-[2.5rem] border border-white/12 bg-white/[0.06] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.28)] backdrop-blur-xl md:p-8 lg:p-10">
              <div className="relative z-10">
                <div className="mb-8 flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/70">
                      {flow.summary.badge}
                    </p>
                    <h3 className="text-2xl font-serif text-foreground">
                      {flow.summary.title}
                    </h3>
                  </div>
                </div>

                <div className="mb-8 rounded-[2rem] border border-white/12 bg-black/20 p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/70">
                    Total
                  </p>
                  <div className="mt-3 flex items-end justify-between gap-4">
                    <div>
                      <p className="text-3xl font-semibold tracking-tight text-foreground">
                        {flow.summary.formatPrice(flow.summary.totalPrice)}
                      </p>
                    </div>
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <Wallet className="h-5 w-5" />
                    </div>
                  </div>
                </div>

                <div className="relative z-10 space-y-8">
                  {flow.summary.selectedPackage && (
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/70">
                            Package
                          </p>
                          <h4 className="max-w-[14rem] text-xl font-serif text-foreground">
                            {flow.summary.selectedPackage.name}
                          </h4>
                        </div>
                        <span className="text-lg font-semibold text-primary">
                          {flow.summary.formatPrice(flow.summary.packagePrice)}
                        </span>
                      </div>
                      <p className="text-xs font-light italic leading-relaxed text-muted-foreground">
                        {flow.summary.selectedPackage.durationMinutes} Minutes Session
                      </p>
                    </div>
                  )}

                  {flow.summary.selectedThemes.length > 0 && (
                    <div className="space-y-3 border-t border-white/10 pt-6">
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                        <ImageIcon className="h-3 w-3" />
                        {flow.summary.selectedThemes.length > 1
                          ? "Selected Themes"
                          : "Selected Theme"}
                      </div>
                      <div className="flex flex-wrap gap-2 text-sm font-medium text-foreground/80">
                        {flow.summary.selectedThemes.map((theme, index) => (
                          <span
                            key={`${theme.id}-${index}`}
                            className="inline-flex items-center rounded-full border border-white/12 bg-black/20 px-3 py-1 text-xs"
                          >
                            {theme.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {flow.summary.themePrice > 0 && flow.summary.selectedThemes[0] && (
                    <div className="space-y-3 border-t border-white/10 pt-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/70">
                            Theme Price
                          </p>
                          <h4 className="max-w-[14rem] text-xl font-serif text-foreground">
                            {flow.summary.selectedThemes[0].name}
                          </h4>
                        </div>
                        <span className="text-lg font-semibold text-primary">
                          {flow.summary.formatPrice(flow.summary.themePrice)}
                        </span>
                      </div>
                    </div>
                  )}

                  {flow.summary.slots.length > 0 && (
                    <div className="space-y-4 border-t border-white/10 pt-6">
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                        <CalendarDays className="h-3 w-3" />
                        Scheduled Sessions
                      </div>
                      <div className="space-y-3">
                        {flow.summary.slots.map((slot, index) => (
                          <div
                            key={`${slot.date}-${slot.time}-${index}`}
                            className="flex items-start gap-3 rounded-[1.25rem] border border-white/12 bg-black/20 p-4"
                          >
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                              <Clock3 className="h-3.5 w-3.5" />
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs font-bold text-foreground">
                                {format(parseISO(slot.date), "MMMM d, yyyy")}
                              </span>
                              <span className="text-[11px] font-semibold uppercase tracking-widest text-primary/70">
                                {slot.time}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {flow.summary.selectedAddons.length > 0 && (
                    <div className="space-y-4 border-t border-white/10 pt-6">
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                        <Gift className="h-3 w-3" />
                        Extra Add-ons
                      </div>
                      <div className="space-y-3">
                        {flow.summary.selectedAddons.map((selectedAddon) => {
                          if (!selectedAddon.addon) {
                            return null;
                          }

                          return (
                            <div
                              key={selectedAddon.addonId}
                              className="flex items-center justify-between rounded-[1.15rem] border border-white/12 bg-black/20 px-4 py-3"
                            >
                              <div className="flex flex-col">
                                <span className="text-[11px] font-bold text-foreground">
                                  {selectedAddon.addon.name}
                                </span>
                                <span className="text-[10px] font-medium text-muted-foreground">
                                  Qty: {selectedAddon.quantity}
                                </span>
                              </div>
                              <span className="text-[11px] font-bold text-primary">
                                {flow.summary.formatPrice(
                                  selectedAddon.addon.price * selectedAddon.quantity
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="border-t border-white/10 pt-6">
                    <div className="space-y-3 rounded-[1.5rem] border border-white/12 bg-black/20 p-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Package</span>
                        <span className="font-semibold text-foreground">
                          {flow.summary.formatPrice(flow.summary.packagePrice)}
                        </span>
                      </div>
                      {flow.summary.themePrice > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Theme</span>
                          <span className="font-semibold text-foreground">
                            {flow.summary.formatPrice(flow.summary.themePrice)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Add-ons</span>
                        <span className="font-semibold text-foreground">
                          {flow.summary.formatPrice(flow.summary.addonsTotal)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-t border-white/10 pt-3">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/70">
                          Total
                        </span>
                        <span className="text-lg font-semibold text-primary">
                          {flow.summary.formatPrice(flow.summary.totalPrice)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.aside>
        )}
      </div>
    </div>
  );
};
